import type { JSX } from 'react';
import React, { useMemo } from 'react';

import type { WithSlotProps } from '~/components/hocs';
import type { DefineSlots } from '~/components/layout/utils';
import { withSlot } from '~/components/hocs';
import { componentRegistry, componentSlots, renderChild } from '~/components/layout/utils';
import { SlotsProvider } from '~/components/providers';
import { sendAppMessage } from '~/embed/utils';
import { useTemplate } from '~/hooks';
import { useGetCommentsCount } from '~/hooks/api/use-get-comments-count';
import {
  selectContentWidth,
  selectIsFullWidthMode,
  selectIsInAppMode,
  selectIsOpen,
  selectVcmId,
  useStore,
} from '~/store';

export const NAME = 'Header';

export const Slots = componentSlots(NAME, {
  TITLE: 'title',
  HEIGHT_MD: 'height-md',
  LOGO_IMG: 'logo-img',
  LOGO: 'logo',
  CLOSE_BTN: 'close-btn',
});

type SlotProps = DefineSlots<
  typeof Slots,
  {
    [Slots.TITLE]: JSX.IntrinsicElements['h1'];
    [Slots.CLOSE_BTN]: JSX.IntrinsicElements['button'];
    [Slots.HEIGHT_MD]: JSX.IntrinsicElements['div'];
    [Slots.LOGO_IMG]: JSX.IntrinsicElements['div'];
    [Slots.LOGO]: JSX.IntrinsicElements['div'];
  }
>;

export interface Props extends React.PropsWithChildren, WithSlotProps {
  className?: string;
  title: string;
}

const HeaderComponent = (props: Props) => {
  const { title } = props;
  const template = useTemplate('Header');
  const closeBtnIconTemplate = useTemplate(useStore(selectIsFullWidthMode) ? 'BackIcon' : 'CloseIcon');
  const isEmbedOpen = useStore(selectIsOpen);
  const isInAppMode = useStore(selectIsInAppMode);
  const { data, isLoading } = useGetCommentsCount({ path: { originId: useStore(selectVcmId) }, enabled: isEmbedOpen });
  const commentsCount = isLoading || !data?.count ? '' : data.count.toString();
  const contentWidth = useStore(selectContentWidth);

  const slots = useMemo<SlotProps>(
    () => ({
      [Slots.CLOSE_BTN]: {
        onClick: () => {
          sendAppMessage(window.parent, {
            type: isInAppMode ? 'onCloseAppClick' : 'onCloseDrawerClick',
          });
        },
        children: renderChild(closeBtnIconTemplate),
      },
      [Slots.HEIGHT_MD]: {
        className: contentWidth <= 1024 ? 'height-md' : '',
      },
      [Slots.TITLE]: {
        children: title.replace('{{count}}', commentsCount),
        className: contentWidth <= 1024 ? 'not-desk' : '',
      },
      [Slots.LOGO_IMG]: {
        className: contentWidth <= 1024 ? 'not-desk' : '',
      },
      [Slots.LOGO]: {
        className: contentWidth <= 1024 ? 'not-desk' : '',
      },
    }),
    [isInAppMode, closeBtnIconTemplate, commentsCount, title, contentWidth]
  );

  if (isInAppMode) {
    return null;
  }

  return <SlotsProvider slots={slots}> {renderChild(template)}</SlotsProvider>;
};

HeaderComponent.displayName = NAME;

export const Header = withSlot(HeaderComponent);
componentRegistry.add(NAME, Header as React.FC);
